/**
 * P0.4 onboarding wizard — data hooks.
 *
 * `useOnboardingState` is called from the mount point in the app shell to
 * decide whether the wizard should be visible. `useAdvanceOnboarding`,
 * `useSkipOnboarding`, and `useCompleteOnboarding` back the CTAs.
 *
 * All mutations invalidate the state query so the wizard re-reads the row
 * (rather than us trying to mirror the FSM on the client).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { OnboardingState, OnboardingStep } from '@seo/types';
import { apiGet, apiPost } from '@/lib/api';
import { useAuthStore } from '@/lib/auth';

const KEY = ['onboarding', 'state'] as const;

export function useOnboardingState() {
  const isAuthed = useAuthStore((s) => Boolean(s.accessToken && s.tenant));
  return useQuery({
    queryKey: KEY,
    queryFn: () => apiGet<OnboardingState>('/v1/onboarding/state'),
    enabled: isAuthed,
    // The wizard mounts on every authenticated page — cache aggressively so
    // navigating between pages doesn't fire a request each time. Mutations
    // invalidate the key manually.
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    retry: false,
  });
}

export function useAdvanceOnboarding() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (to_step: OnboardingStep) =>
      apiPost<OnboardingState>('/v1/onboarding/advance', { to_step }),
    onSuccess: (state) => {
      qc.setQueryData(KEY, state);
    },
  });
}

export function useSkipOnboarding() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (step: OnboardingStep) =>
      apiPost<OnboardingState>('/v1/onboarding/skip', { step }),
    onSuccess: (state) => {
      qc.setQueryData(KEY, state);
    },
  });
}

export function useCompleteOnboarding() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiPost<OnboardingState>('/v1/onboarding/complete'),
    onSuccess: (state) => {
      qc.setQueryData(KEY, state);
    },
  });
}

/** Reset to `create_project` with empty skip list. Backs the "Restart" button. */
export function useResetOnboarding() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiPost<OnboardingState>('/v1/onboarding/reset'),
    onSuccess: (state) => {
      qc.setQueryData(KEY, state);
    },
  });
}

/** Move back one step. Idempotent at step 1. */
export function useBackOnboarding() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiPost<OnboardingState>('/v1/onboarding/back'),
    onSuccess: (state) => {
      qc.setQueryData(KEY, state);
    },
  });
}
